import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, isLoaded: userLoaded } = useUser();
  const { getToken } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);
  const [trial, setTrial] = useState(null);
  const [trashCount, setTrashCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [synced, setSynced] = useState(false);

  // Authenticated fetch helper
  const apiFetch = useCallback(async (path, options = {}) => {
    const token = await getToken();
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  }, [getToken]);

  // Step 1: Sync user to DB (creates row if missing, checks trial expiry)
  const syncUser = useCallback(async () => {
    if (!user) return;
    try {
      await apiFetch('/api/auth/sync', {
        method: 'POST',
        body: JSON.stringify({
          email: user.emailAddresses?.[0]?.emailAddress,
          firstName: user.firstName,
          lastName: user.lastName,
          avatarUrl: user.imageUrl,
        }),
      });
      setSynced(true);
    } catch (e) {
      console.error('[SubscriptionProvider] User sync failed:', e);
      setSynced(true); // Still proceed so app doesn't block
    }
  }, [user, apiFetch]);

  // Step 2: Load subscription data
  const loadSubscription = useCallback(async () => {
    try {
      const data = await apiFetch('/api/subscription');
      setSubscription(data.subscription || { tier: 'free', status: 'active' });
      setUsage(data.usage || {});
      setLimits(data.limits || { websites: 2, notes_per_website: 50, members: 3 });
      setTrial(data.trial || { active: false, days_left: 0 });
      setTrashCount(data.trash_count || 0);
    } catch (e) {
      console.error('[SubscriptionProvider] Load subscription failed:', e);
      setSubscription({ tier: 'free', status: 'active' });
      setUsage({});
      setLimits({ websites: 2, notes_per_website: 50, members: 3 });
      setTrial({ active: false, days_left: 0 });
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  // Run on user load
  useEffect(() => {
    if (!userLoaded || !user) {
      setLoading(false);
      return;
    }
    syncUser();
  }, [userLoaded, user, syncUser]);

  // Once synced, load subscription
  useEffect(() => {
    if (synced) loadSubscription();
  }, [synced, loadSubscription]);

  // Derived helpers
  const tier = subscription?.tier || 'free';
  // access_tier = highest tier across own subscription + collaborations
  // Used for feature gating (nav items, UI locks) — collaborators inherit owner features
  const accessTier = subscription?.access_tier || tier;
  const isPro = accessTier === 'pro' || accessTier === 'team';
  const isTeam = accessTier === 'team';
  const isFree = accessTier === 'free';
  // Whether this user personally owns a paid subscription (for billing UI)
  const isOwnPaidTier = tier === 'pro' || tier === 'team';
  const isTrialing = subscription?.status === 'trialing';
  const hasSubscription = !!subscription?.has_subscription;
  const billingInterval = subscription?.billing_interval || null;
  const billableMembers = subscription?.billable_members || 0;
  const basePrice = subscription?.base_price || 0;
  const memberPrice = subscription?.member_price || 0;
  const monthlyTotal = subscription?.monthly_total || 0;

  // Refresh subscription data (call after upgrade, trial start, etc.)
  const refresh = useCallback(() => {
    return loadSubscription();
  }, [loadSubscription]);

  const value = {
    // State
    subscription,
    usage,
    limits,
    trial,
    trashCount,
    loading,

    // Derived
    tier,
    accessTier,
    isPro,
    isTeam,
    isFree,
    isOwnPaidTier,
    isTrialing,
    hasSubscription,
    billingInterval,
    billableMembers,
    basePrice,
    memberPrice,
    monthlyTotal,

    // Actions
    refresh,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
